import React from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { posts } from '../data/blog';

const fade = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
};

const stagger = {
  animate: { transition: { staggerChildren: 0.1 } },
};

const Page = styled(motion.div)`
  min-height: 100vh;
  padding: 10rem 2.5rem 6rem;
  max-width: 1100px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 7rem 1.25rem 4rem;
  }
`;

const Header = styled.div`
  margin-bottom: 4rem;
`;

const Title = styled(motion.h1)`
  font-family: var(--font-display);
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 300;
  letter-spacing: -0.01em;
  margin-bottom: 1rem;
`;

const Subtitle = styled(motion.p)`
  font-family: var(--font-mono);
  font-size: 0.78rem;
  color: var(--text-muted);
  letter-spacing: 0.04em;
`;

const PostList = styled(motion.div)`
  display: flex;
  flex-direction: column;
`;

const PostLink = styled(Link)`
  display: block;
  color: inherit;

  &:hover {
    color: inherit;
  }
`;

const PostEntry = styled(motion.article)`
  display: grid;
  grid-template-columns: 60px 1fr auto;
  gap: 2rem;
  align-items: baseline;
  padding: 2rem 0;
  border-top: 1px solid var(--border);
  cursor: pointer;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -1px;
    left: 0;
    width: 0;
    height: 1px;
    background: var(--accent);
    transition: width 0.4s ease;
  }

  &:hover::before {
    width: 100%;
  }

  &:hover h2 {
    color: var(--accent);
  }

  @media (max-width: 768px) {
    grid-template-columns: 40px 1fr;
    gap: 1rem;
  }
`;

const PostNumber = styled.span`
  font-family: var(--font-display);
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--border);
`;

const PostContent = styled.div``;

const PostTitle = styled.h2`
  font-family: var(--font-display);
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 0.35rem;
  transition: color 0.2s ease;
  line-height: 1.3;
`;

const PostExcerpt = styled.p`
  font-size: 0.85rem;
  color: var(--text-muted);
  line-height: 1.6;
  max-width: 540px;
`;

const PostDate = styled.span`
  font-family: var(--font-mono);
  font-size: 0.68rem;
  color: var(--text-muted);
  letter-spacing: 0.04em;
  white-space: nowrap;

  @media (max-width: 768px) {
    display: none;
  }
`;

const PostTag = styled.span`
  font-family: var(--font-mono);
  font-size: 0.6rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--accent);
  margin-top: 0.6rem;
  display: inline-block;
`;

const EmptyState = styled(motion.div)`
  text-align: center;
  padding: 6rem 0;
`;

const EmptyTitle = styled.h2`
  font-family: var(--font-display);
  font-size: 1.5rem;
  font-weight: 400;
  margin-bottom: 0.75rem;
`;

const EmptyText = styled.p`
  font-size: 0.9rem;
  color: var(--text-muted);
`;

const Blog: React.FC = () => (
  <Page
    initial="initial"
    animate="animate"
    exit="exit"
    variants={fade}
    transition={{ duration: 0.5 }}
  >
    <Header>
      <Title variants={fade}>Blog</Title>
      <Subtitle variants={fade}>
        Writing about engineering, AI, and building things.
      </Subtitle>
    </Header>

    {posts.length > 0 ? (
      <PostList variants={stagger} initial="initial" animate="animate">
        {posts.map((post, i) => (
          <PostLink key={post.slug} to={`/blog/${post.slug}`}>
            <PostEntry variants={fade}>
              <PostNumber>{String(i + 1).padStart(2, '0')}</PostNumber>
              <PostContent>
                <PostTitle>{post.title}</PostTitle>
                <PostExcerpt>{post.excerpt}</PostExcerpt>
                <PostTag>{post.tag}</PostTag>
              </PostContent>
              <PostDate>{post.date}</PostDate>
            </PostEntry>
          </PostLink>
        ))}
      </PostList>
    ) : (
      <EmptyState variants={fade}>
        <EmptyTitle>Nothing here yet</EmptyTitle>
        <EmptyText>Posts are on the way. Check back soon.</EmptyText>
      </EmptyState>
    )}
  </Page>
);

export default Blog;
